import { postToBackend } from "./portalClient";
import { withPaymentMethod } from "./paymentMethod";

/**
 * Post-booking hotel MODIFY (new dates and/or a new room) and CANCEL from the
 * CRM and the partner portal. The calls pass no portal, so they go out on the
 * session of the page that makes them (`postToBackend` → `activePortal()`);
 * the portal is named in the body as `userType` (`modifyContextFor`).
 *
 * Same contract, same bodies, same verb as the traveller app's "Modify
 * Reservation" flow — see HOTEL_MODIFY_CANCEL_FLOW.md §5 and app-frontend's
 * useModifyHotelReservationLogic.ts:
 *
 *   POST /app/v1/trips/hotels/change          rates for the new dates
 *   POST /app/v1/trips/hotels/rebook          prebook the picked rate(s)
 *   POST /app/v1/trips/hotels/rebook/confirm  commit, after payment (Path A)
 *
 * Differences from the traveller app, all deliberate:
 *  - Payment is collected INSIDE the portal (BookingChargeModal: partner wallet
 *    via finance/transactions/create, or UPI), not by redirecting to the
 *    `paymentLink` rebook returns. `paymentId` on confirm is therefore the
 *    wallet transaction's UUID (or the UPI charge id).
 *  - `PUT users/itineraries` (step 8, trip dates) is NOT called: this transport
 *    is POST-only by platform rule, and the CRM re-reads the booking from
 *    /common/transaction/get after a modify, which already reflects the new
 *    dates. The traveller app only logs a failure of that call anyway.
 *  - Currency is the booking's own (the traveller app hardcodes USD).
 *  - The `change` search also carries the partner context — `partnerCode`,
 *    `branchCode`, `userType`, `bookingChannel`, `partnerCurrency`
 *    (`modifyContextFor`). `rebook` and `rebook/confirm` do not.
 *
 * CANCEL (HOTEL_MODIFY_CANCEL_FLOW.md §4) — two calls, both with the same
 * partner context in front of the traveller app's body:
 *
 *   POST /app/v1/trips/hotels/refund   quote; `hotels` = each stay's
 *                                      transactionTid (NOT hotelBookId)
 *   POST /app/v1/trips/hotels/cancel   commit; `hotel[].hotelBookId`
 *
 * There is no cancellation reason; the backend decides refund eligibility.
 *
 * Unwrapped shapes (portalClient strips the outer `{statusCode, data}`):
 *   change  → result.data = { data: [{ hotelBookId, sessionId, rooms[] }] }
 *   rebook  → result.data = { priceChanged, itineraryID, paymentLink,
 *                             idempotencyKey, hotel: [{ hotelBookId?,
 *                             prebookId, sessionId, modifiedHotelDetails }] }
 * The top-level `priceChanged` copy is dropped by the unwrap; `data.priceChanged`
 * carries the same flag.
 */

/**
 * `context` is the partner context the change search needs on top of the
 * traveller app's body — see `modifyContextFor` below. Spread first so the
 * body reads partnerCode … partnerCurrency, then userId, itineraryId, hotel.
 */
export const searchModifyRooms = ({ context, userId, itineraryId, hotel }) =>
  postToBackend("/app/v1/trips/hotels/change", {
    ...(context || {}),
    userId,
    itineraryId,
    hotel,
  });

// `paymentDetails.paymentMethod` is required by the engine — see paymentMethod.js.
export const rebookHotels = ({ id, userId, paymentDetails, hotels }) =>
  postToBackend("/app/v1/trips/hotels/rebook", {
    id,
    userId,
    paymentDetails: withPaymentMethod(paymentDetails),
    hotels,
  });

/**
 * The key goes on the wire under the tag `Idempotency-Key` (backend contract,
 * 2026-10-05), with the value the rebook response returned as
 * `data.idempotencyKey`. Omitted when the rebook minted none, so a stale key
 * is never echoed.
 */
export const confirmHotelRebook = ({
  userId,
  paymentId,
  id,
  hotels,
  idempotencyKey,
}) =>
  postToBackend("/app/v1/trips/hotels/rebook/confirm", {
    userId,
    paymentId,
    id,
    hotels,
    ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
  });

/** Per-stay availability list, tolerant of one extra level of wrapping. */
export const extractChangeStays = (result) => {
  const d = result?.data;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data?.data)) return d.data.data;
  return [];
};

/** The rebook payload, tolerant of one extra level of wrapping. */
export const extractRebook = (result) => {
  const d = result?.data || {};
  if (d.hotel || d.itineraryID || d.paymentLink) return d;
  return d.data || d;
};

/** Cancel step 1 — refund quote for the selected stays' transactionTids. */
export const quoteHotelRefund = ({ context, userId, hotels }) =>
  postToBackend("/app/v1/trips/hotels/refund", {
    ...(context || {}),
    userId,
    hotels,
  });

/** Cancel step 2 — cancel the selected stays by hotelBookId. */
export const cancelHotels = ({ context, userId, itineraryId, id, hotel }) =>
  postToBackend("/app/v1/trips/hotels/cancel", {
    ...(context || {}),
    userId,
    itineraryId,
    id,
    hotel,
  });

/**
 * The refund quote as numbers, tolerant of one extra level of wrapping.
 * `totalRefundAmount` goes back to the customer; `totalPromoDiscount` was
 * applied at booking time and is not refunded.
 */
export const extractRefundQuote = (result) => {
  const d = result?.data || {};
  const q = "totalRefundAmount" in d || "totalPromoDiscount" in d ? d : d.data || {};
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  return {
    totalRefundAmount: num(q.totalRefundAmount),
    totalPromoDiscount: num(q.totalPromoDiscount),
  };
};

/**
 * Customer-safe message per HOTEL_MODIFY_CANCEL_FLOW.md §2.4 — flat shape,
 * then the wrapped `rawError` shape, then `data` (rebook-confirm failures).
 * Returns null when the backend sent none, so callers pick their own copy.
 */
export const pickDisplayMessage = (result) =>
  result?.displayMessage ||
  result?.rawError?.displayMessage ||
  result?.rawError?.details?.displayMessage ||
  result?.errorData?.displayMessage ||
  result?.errorData?.details?.displayMessage ||
  null;

export const errorTitleFor = (result) =>
  result?.statusCode === 422 ? "Not allowed" : "Request failed";

/** Everything support needs to trace a failure; never shown to the operator.
 *  `tag` names the flow in the log line (the activity flows pass their own). */
export const logModifyError = (step, result, tag = "hotel-modify") => {
  // eslint-disable-next-line no-console
  console.warn(`[${tag}:${step}]`, {
    statusCode: result?.statusCode,
    message: result?.message,
    errorReference: result?.errorReference || result?.rawError?.errorReference,
    reasonCode:
      result?.rawError?.details?.reasonCode || result?.errorData?.reasonCode,
    validationErrors:
      result?.rawError?.details?.validationErrors ||
      result?.errorData?.validationErrors,
  });
};

/**
 * The ids the modify endpoints need, resolved in ONE place.
 *
 * The CRM /get response does not carry the traveller app's ids yet, so each
 * one prefers the field the backend is expected to add and falls back to the
 * closest id the CRM already has:
 *
 *   userId       booking owner (`userId`, else `bookedByUserId`). No fake
 *                fallback — the caller must refuse to modify without one.
 *   itineraryId  engine itinerary id = `bookingRef` (booking.id).
 *   tripId       app trip id, sent as `id` on rebook / confirm. Reads
 *                `appItineraryId` / `tripId` — NOT a top-level `id`, which would
 *                flip normalizeBooking onto its legacy branch — else bookingRef.
 *   hotelBookId  per stay: the hotel row's `confirmationId` (what the engine
 *                keys a booked stay by), else an explicit `hotelBookId` if /get
 *                ever sends one. Never `hotelRowId` — that is the CRM's own row
 *                id and the engine doesn't know it; null means the stay can't
 *                be modified or cancelled from here and the modals say so.
 */
export const resolveModifyIds = (booking, sector, details) => ({
  userId: booking?.userId || null,
  itineraryId: booking?.id || null,
  tripId: booking?.appItineraryId || booking?.id || null,
  hotelBookId:
    sector?._api?.confirmationId ||
    sector?._api?.hotelBookId ||
    details?.hotelBookId ||
    null,
});

/**
 * The partner context the `change`, `refund` and `cancel` calls send in front
 * of the traveller app's body.
 *
 *   partnerCode      booking's partner, as a string (/get sends a number)
 *   branchCode       booking's branch, as a string
 *   userType         the portal running the flow — "CRM" (default) or
 *                    "PARTNER"; the modals take it as a prop
 *   bookingChannel   /get's top-level `channel`, uppercased ("B2B" / "B2C")
 *   partnerCurrency  the currency the page labels this booking's money in
 *                    (partner tenant currency, else the booking's own) — the
 *                    modal's `currency` prop — else /get's `currency`
 *
 * A value the booking does not have is left out, never sent as "null".
 */
export const modifyContextFor = (booking, currency, userType = "CRM") => {
  const str = (v) => (v === null || v === undefined ? "" : String(v).trim());
  const ctx = {};
  const partnerCode = str(booking?.partnerCode);
  const branchCode = str(booking?.branchCode);
  const bookingChannel = str(booking?.channel).toUpperCase();
  const partnerCurrency = str(currency || booking?.currency).toUpperCase();
  if (partnerCode) ctx.partnerCode = partnerCode;
  if (branchCode) ctx.branchCode = branchCode;
  ctx.userType = userType === "PARTNER" ? "PARTNER" : "CRM";
  if (bookingChannel) ctx.bookingChannel = bookingChannel;
  if (partnerCurrency) ctx.partnerCurrency = partnerCurrency;
  return ctx;
};
